import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ModuSignStartDocumentRequest, ModuSignTemplate, ModuSignTemplateModel } from "src/api/contract/modusign-types";
import { BaseButton } from "src/components";
import { BaseStep } from "src/components/BaseStep";
import { formatPhoneNumber, YmdFormat } from "src/utils";
import useModuSign from "../../hooks/useModuSign";
import ModuSignStep1 from "./ModuSignStep1";
import ModuSignStep2 from "./ModuSignStep2";
import ModuSignStep3 from "./ModuSignStep3";
import { useModal } from "src/recoil/modalState/hook";

const steps = [
  {
    stepNumber: 0,
    label: "템플릿 선택",
  },
  {
    stepNumber: 1,
    label: "문서 설정",
  },
  {
    stepNumber: 2,
    label: "확인 및 전송",
  },
];

type Props = {
  onClose: () => void;
  setEditable?: (bool: boolean) => void;
  onSubmit: (data: ModuSignStartDocumentRequest) => void;
  openModuSignDocumentPreview: (moduSignStartDocument: ModuSignStartDocumentRequest) => void;
};

const ModuSignAddModal = ({ onClose, setEditable, onSubmit, openModuSignDocumentPreview }: Props) => {
  const [activeTab, setActiveTab] = useState(0);
  const [moduSignTemplates, setModuSignTemplates] = useState<ModuSignTemplateModel>();

  // 모두싸인 템플릿 조회 api 커스텀훅
  const { fetchModuSignTemplateList, fetchModuSignTemplatePreview } = useModuSign();

  // useForm 설정
  const useFormReturn = useForm<ModuSignStartDocumentRequest>({
    defaultValues: {
      templateId: "",
      document: {
        title: "",
        participantMappingsList: [], // 받는 사람 리스트
      },
    },
  });

  const { handleSubmit, setValue, watch, reset } = useFormReturn;

  // 모두싸인 템플릿 미리보기 페이지로 이동
  const getModuSignTemplatePreview = async (templateId: string) => {
    try {
      const moduSignTemplateInfo = await fetchModuSignTemplatePreview(templateId);
      const templateUrl = moduSignTemplateInfo.file.downloadUrl;
      window.open(templateUrl, "_blank");
    } catch (error) {
      console.error("문서 미리보기 실패:", error);
    }
  };

  useEffect(() => {
    const execute = async () => {
      try {
        const moduSignTemplateList = await fetchModuSignTemplateList();
        setModuSignTemplates(moduSignTemplateList);
      } catch (error) {
        console.error("문서 전송 실패:", error);
      }
    };
    execute();
  }, []); // id 의존성 추가

  const onValidationNextStep = (_: ModuSignStartDocumentRequest) => {
    setActiveTab(activeTab + 1);
  };

  const onSetModuSignDocument = (template: ModuSignTemplate) => {
    // 템플릿 id 저장
    setValue("templateId", template?.id || "");
    // 문서명 저장
    setValue("document.title", `${template?.title}_${moment().format(YmdFormat.YYYY_MM_DD)}` || "");
    //전자서명 > 받는 사람 리스트 설정
    const participantMappings = template?.participants.map((participant) => ({
      role: participant.role,
      name: participant.name,
      signingMethod: { type: participant.signingMethod.type || "EMAIL", value: participant.signingMethod.value || "" },
      locale: participant.locale || "ko",
    }));

    setValue("document.participantMappingsList", participantMappings);
  };
  // 기획서 2-4. 이메일 또는 휴대폰 번호 InputBox (필수 입력) 볼 차례
  return (
    <>
      <section className="base-abstract-modal__title flex-center-between border-bottom-gray ">
        <h1>전자서명 요청</h1>
        <div>
          <BaseStep stepList={steps.map((step) => step.label)} selectedStepIndex={activeTab} />
        </div>
      </section>

      <section className="base-abstract-modal__contents ">
        {activeTab === 0 && (
          <ModuSignStep1
            moduSignTemplates={moduSignTemplates?.templates || []}
            getModuSignTemplatePreview={getModuSignTemplatePreview}
            onSelectTemplate={(template: ModuSignTemplate) => {
              onSetModuSignDocument(template);
            }}
            templateId={watch("templateId") as string}
          />
        )}
        {activeTab === 1 && <ModuSignStep2 useFormReturn={useFormReturn} />}
        {activeTab === 2 && <ModuSignStep3 useFormReturn={useFormReturn} openModuSignDocumentPreview={openModuSignDocumentPreview} />}
      </section>
      <section className="base-abstract-modal__btn-wrap flex-center-between">
        <BaseButton
          className="color-white"
          title="취소"
          onClick={() => {
            onClose();
            reset();
          }}
        />
        <div className="flex-center">
          {activeTab > 0 && (
            <BaseButton
              title="이전"
              className="color-white mr10"
              onClick={() => {
                if (activeTab === 1) {
                  reset();
                  setActiveTab(0);
                } else {
                  setActiveTab(activeTab - 1);
                }
              }}
            />
          )}

          {activeTab < steps.length - 1 ? (
            <BaseButton title="다음" onClick={handleSubmit(onValidationNextStep)} disabled={!watch("templateId")} />
          ) : (
            <BaseButton title="전송" onClick={handleSubmit(onSubmit)} />
          )}
        </div>
      </section>
    </>
  );
};

export default ModuSignAddModal;
